import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";

export interface UseTileInsertOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const formatTileText = (tile: ReviewTileItem): string => {
  let text = "";

  if (tile.clause != null) {
    text += `**${tile.clause}**\n\n`;
  }

  if (tile.reason != null) {
    text += `**Reason:** ${tile.reason}\n\n`;
  }

  if (tile.analysis != null) {
    text += `**Analysis:** ${tile.analysis}\n\n`;
  }

  if (tile.explanation != null) {
    text += `**Explanation:** ${tile.explanation}\n\n`;
  }

  return text.trim();
};

const extractSuggestedClause = (tile: ReviewTileItem): string | null => {
  const originalData = tile.originalData;
  if (originalData == null) return null;

  if ("suggestedclause" in originalData && originalData.suggestedclause)
    return originalData.suggestedclause;
  if ("suggestion" in originalData && originalData.suggestion) return originalData.suggestion;
  if ("suggestions" in originalData && originalData.suggestions) return originalData.suggestions;
  if ("suggestedNumber" in originalData && originalData.suggestedNumber)
    return originalData.suggestedNumber;
  return null;
};

async function insertSingleTileIntoWord(tile: ReviewTileItem): Promise<string> {
  let successMessage = "";

  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();

    const selectedText = selection.text;
    const suggestedClause = extractSuggestedClause(tile);

    if (suggestedClause != null) {
      if (selectedText.length > 0) {
        selection.insertText(suggestedClause, Word.InsertLocation.replace);
        successMessage = "Selected text replaced with suggested clause.";
      } else {
        selection.insertText(suggestedClause, Word.InsertLocation.end);
        successMessage = "Suggested clause inserted.";
      }
    } else {
      selection.insertText(formatTileText(tile) + "\n\n", Word.InsertLocation.end);
      successMessage = `Tile "${tile.clause ?? tile.id}" inserted successfully`;
    }

    await context.sync();
  });

  return successMessage;
}

async function insertMultipleTilesIntoWord(tiles: ReviewTileItem[]): Promise<string> {
  let successMessage = "";

  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();

    const selectedText = selection.text;

    if (selectedText.length > 0) {
      const firstTile = tiles[0];
      if (firstTile == null) {
        throw new Error("No tiles available for insertion");
      }
      const suggestedClause = extractSuggestedClause(firstTile);

      if (suggestedClause != null) {
        selection.insertText(suggestedClause, Word.InsertLocation.replace);
        if (tiles.length > 1) {
          const remainingText = tiles.slice(1).map(formatTileText).join("\n\n---\n\n");
          selection.insertText("\n\n---\n\n" + remainingText + "\n\n", Word.InsertLocation.end);
        }
        successMessage = `Selected text replaced with suggested clause. ${tiles.length} tiles processed.`;
      } else {
        const allText = tiles.map(formatTileText).join("\n\n---\n\n");
        selection.insertText(allText + "\n\n", Word.InsertLocation.end);
        successMessage = `${tiles.length} tiles inserted successfully`;
      }
    } else {
      const allText = tiles.map(formatTileText).join("\n\n---\n\n");
      selection.insertText(allText + "\n\n", Word.InsertLocation.end);
      successMessage = `${tiles.length} tiles inserted successfully`;
    }

    await context.sync();
  });

  return successMessage;
}

export const useTileInsert = (options?: UseTileInsertOptions) => {
  const [isInserting, setIsInserting] = useState(false);
  const [insertingTileId, setInsertingTileId] = useState<string | null>(null);

  const insertSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setIsInserting(true);
    setInsertingTileId(tile.id);
    try {
      const message = await insertSingleTileIntoWord(tile);
      options?.onSuccess?.(message);
      return true;
    } catch (error: unknown) {
      console.error("Error inserting tile:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to insert tile: ${msg}`);
      return false;
    } finally {
      setIsInserting(false);
      setInsertingTileId(null);
    }
  };

  const insertMultipleTiles = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to insert");
      return false;
    }
    setIsInserting(true);
    try {
      const message = await insertMultipleTilesIntoWord(tiles);
      options?.onSuccess?.(message);
      return true;
    } catch (error: unknown) {
      console.error("Error inserting tiles:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to insert tiles: ${msg}`);
      return false;
    } finally {
      setIsInserting(false);
    }
  };

  return {
    isInserting,
    insertingTileId,
    insertSingleTile,
    insertMultipleTiles,
  };
};
